import type { IntegrationCapability, IntegrationKind } from "@lumen/shared";
import {
  Boxes,
  BriefcaseBusiness,
  CalendarDays,
  CircleHelp,
  Clock3,
  FileText,
  Github,
  Mail,
  Megaphone,
  Send,
  ShieldCheck,
  Webhook,
} from "lucide-react";

export type Config = Record<string, string | boolean>;
type FieldType = "text" | "url" | "email" | "number" | "password";

export type Field = {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  type?: FieldType;
  secret?: boolean;
  multiline?: boolean;
  required?: boolean;
  defaultValue?: string;
  advanced?: boolean;
};

export type Capability = {
  key: IntegrationCapability | "send_messages";
  label: string;
};

export type IntegrationDefinition = {
  kind: IntegrationKind;
  label: string;
  group: string;
  description: string;
  setup: string;
  capabilities: Capability[];
  fields: Field[];
};

export type Draft = {
  name: string;
  scope: "personal" | "organization";
  kind: IntegrationKind;
  config: Config;
};

export type ActionApproval = {
  id: string;
  integrationId: string;
  action: string;
  status:
    | "pending"
    | "approved"
    | "denied"
    | "executing"
    | "executed"
    | "failed"
    | "expired";
  expiresAt: string;
  createdAt: string;
};

const capabilityLabels: Record<string, string> = {
  receive_events: "Receive events",
  send_messages: "Send messages",
  send_media: "Send media",
  calendar: "Calendar actions",
  files: "File context",
  crm: "CRM actions",
  tickets: "Support tickets",
  repositories: "Repository actions",
  social: "Social publishing",
  marketing_email: "Email campaigns",
  analytics: "Analytics reports",
  leads: "Lead capture",
  pos: "POS data",
  orders: "Orders & menus",
  reservations: "Reservations",
  delivery: "Delivery tracking",
  accounting: "Accounting data",
  e_invoicing: "E-invoicing",
  payments: "Payment data",
  payroll: "Payroll & staff",
  reviews: "Reviews & reputation",
  business_registry: "Business verification",
  food_data: "Food data",
  procurement: "Procurement data",
};

export const capabilityIcons: Record<string, typeof Send> = {
  receive_events: Webhook,
  send_messages: Send,
  send_media: FileText,
  calendar: CalendarDays,
  files: FileText,
  crm: BriefcaseBusiness,
  tickets: CircleHelp,
  repositories: Github,
  social: Megaphone,
  marketing_email: Mail,
  analytics: Clock3,
  leads: BriefcaseBusiness,
  pos: Boxes,
  orders: Boxes,
  reservations: CalendarDays,
  delivery: Send,
  accounting: FileText,
  e_invoicing: FileText,
  payments: ShieldCheck,
  payroll: BriefcaseBusiness,
  reviews: CircleHelp,
  business_registry: ShieldCheck,
  food_data: FileText,
  procurement: Boxes,
};

export { capabilityLabels };

export const textField = (
  key: string,
  label: string,
  description: string,
  options: Omit<Field, "key" | "label" | "description"> = {},
): Field => ({ key, label, description, ...options });

export const tokenField = (
  label = "Access token",
  description = "Create a token in the provider and paste it here.",
): Field =>
  textField("accessToken", label, description, {
    secret: true,
    required: true,
    placeholder: "Paste token",
  });
